import { ChartPie, ListFilter } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DistributionFilterMenu } from "./DistributionFilterMenu";
import { DistributionLegendList } from "./DistributionLegendList";
import { DistributionPie } from "./DistributionPie";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { ROUTES } from "@/constants/routes";
import {
  type ActiveCampaignDistributionItem,
  useActiveCampaignDistribution,
} from "@/features/home/api/useActiveCampaignDistribution";
import { useDistributionSlices } from "@/features/home/hooks/useDistributionSlices";
import { encodeSlug } from "@/features/integrations/utils/slug";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { useVisibleEntities } from "@/lib/hooks/useVisibleEntities";
import { resolveNumberLocale } from "@/utils/valueFormat";

export function ActiveCampaignDistributionSection({
  clientId,
}: {
  clientId?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canView } = usePermissionMatrix();
  const canOpenIntegration = canView("integrations");
  const {
    rows: distribution,
    totalCampaignCount,
    isLoading,
  } = useActiveCampaignDistribution(clientId);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const {
    selected,
    filterActive,
    isSelected: isVisible,
    toggle,
    reset,
  } = useVisibleEntities<ActiveCampaignDistributionItem>({
    items: distribution,
    storageKey: "home.activeCampaignDistribution.visible",
    getKey: (d) => d.name,
    defaultSelection: (items) => items.map((d) => d.name),
  });

  const { rows, chartConfig, hasData } = useDistributionSlices({
    distribution,
    selected,
    t,
    i18n,
  });

  const goToIntegration = (name: string) => {
    const base = clientId
      ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}`
      : `/${ROUTES.integrations}`;
    navigate(`${base}/${encodeSlug(name)}`);
  };

  const filteredEverythingOut =
    !isLoading && distribution.length > 0 && !hasData;

  // Pinned presentation locale, not the interface language — see @/utils/valueFormat.
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(resolveNumberLocale()),
    [],
  );
  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(resolveNumberLocale(), {
        style: "percent",
        maximumFractionDigits: 0,
      }),
    [],
  );

  return (
    <Card padding="md" className="h-[650px] py-5 w-full">
      <CardHeader className="flex flex-row items-start justify-between gap-3 px-5 space-y-0">
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle className="text-lg">
            {t("home.activeCampaignDistribution.title")}
          </CardTitle>
          <CardDescription>
            {t("home.activeCampaignDistribution.subtitle")}
          </CardDescription>
        </div>
        {distribution.length > 0 && (
          <DistributionFilterMenu
            distribution={distribution}
            filterActive={filterActive}
            isVisible={isVisible}
            toggle={toggle}
            reset={reset}
            numberFormatter={numberFormatter}
            t={t}
            i18n={i18n}
          />
        )}
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col justify-center px-5">
        {isLoading ? (
          <DistributionSkeleton />
        ) : filteredEverythingOut ? (
          <EmptyState
            icon={ListFilter}
            title={t("home.activeCampaignDistribution.filter.emptyTitle")}
            description={t(
              "home.activeCampaignDistribution.filter.emptyDescription",
            )}
            action={{
              label: t("home.activeCampaignDistribution.filter.reset"),
              onClick: reset,
            }}
          />
        ) : !hasData ? (
          <EmptyState
            icon={ChartPie}
            title={t("home.activeCampaignDistribution.empty.title")}
            description={t("home.activeCampaignDistribution.empty.description")}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center gap-6">
            <DistributionPie
              rows={rows}
              chartConfig={chartConfig}
              totalCampaignCount={totalCampaignCount}
              hoveredKey={hoveredKey}
              onHover={setHoveredKey}
              numberFormatter={numberFormatter}
              percentFormatter={percentFormatter}
              t={t}
            />
            <DistributionLegendList
              rows={rows}
              hoveredKey={hoveredKey}
              onHover={setHoveredKey}
              canOpenIntegration={canOpenIntegration}
              onOpen={goToIntegration}
              numberFormatter={numberFormatter}
              t={t}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DistributionSkeleton() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <Skeleton className="aspect-square w-[80%] min-w-[200px] max-w-[400px] shrink-0 rounded-full" />
      <div className="flex w-full max-w-md flex-col gap-1.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-2 py-1.5">
            <Skeleton className="size-2.5 rounded-full" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}
